import {
  Flex,
  Link,
  List,
  TagFilledIcon,
  Typography,
} from '@iheartjane/reefer';
import type {
  ComplianceLanguageSettings,
  SpecialType,
} from '@iheartjane/types';
import { useMemo, useState } from 'react';

import { getSpecialType } from '../../utils/specials/storeSpecial';
import { SpecialCallout } from '../specialCallout/specialCallout';

import styles from './specialCalloutSection.module.css';

export interface SpecialCalloutItemData {
  applicableWeights?: string[];
  id: number;
  isApplied: boolean;
  isQualified: boolean;
  special_type: string;
  title: string;
}

export interface SpecialCalloutSectionProps {
  complianceLanguageSettings?: ComplianceLanguageSettings | null;
  extendedDealsLabelsEnabled?: boolean;
  isAuthenticated?: boolean;
  isGold?: boolean;
  onSpecialClick: (special: SpecialCalloutItemData) => void;
  sectionLabel?: string;
  specials: SpecialCalloutItemData[];
}

const LABEL_ID = 'button-label';
export const SpecialCalloutSection = ({
  complianceLanguageSettings,
  extendedDealsLabelsEnabled = false,
  isAuthenticated = false,
  isGold = false,
  onSpecialClick,
  sectionLabel = 'Deals',
  specials,
}: SpecialCalloutSectionProps) => {
  const [viewAll, setViewAll] = useState(false);

  const displayedSpecials = useMemo(() => {
    return specials.slice(0, viewAll ? undefined : 3);
  }, [specials, viewAll]);

  if (specials.length === 0) {
    return null;
  }

  return (
    <Flex flexDirection="column">
      <Flex gap={8} alignItems="center">
        <TagFilledIcon color="info" size="sm" />
        <Typography variant="body-bold">{sectionLabel}</Typography>
      </Flex>
      <List label={`List of ${sectionLabel}`}>
        {displayedSpecials.map((special) => (
          <li key={special.id} className={styles['list-item-y-padding']}>
            <SpecialCallout
              applicableWeights={special.applicableWeights}
              isApplied={special.isApplied}
              isAuthenticated={isAuthenticated}
              isGold={isGold}
              isQualified={special.isQualified}
              onClick={() => onSpecialClick(special)}
              specialTitle={special.title}
              specialType={special.special_type}
              specialTypeLabel={getSpecialType(
                special.special_type as SpecialType,
                complianceLanguageSettings,
                extendedDealsLabelsEnabled
              )}
              variant="pdp"
            />
          </li>
        ))}
        {specials.length > 3 && (
          <Flex
            justifyContent="center"
            aria-labelledby={LABEL_ID}
            py={8}
            onClick={() => setViewAll(!viewAll)}
          >
            <Link
              className={styles['inner-link']}
              id={LABEL_ID}
            >{`View ${viewAll ? 'less' : 'all'}`}</Link>
          </Flex>
        )}
      </List>
    </Flex>
  );
};
